import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bus, CheckCircle2, Clock, Ban } from 'lucide-react';
import api from '../../../services/api';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import { createTrip, updateTrip, getTripDetail, setTripFares } from '../../../services/trips';
import { getRoute, listRoutes } from '../../../services/routes';
import { listVehicles } from '../../../services/vehicles';
import { listDrivers } from '../../../services/drivers';
import RouteFareMatrix from '../../../components/admin/RouteFareMatrix';
import { BulkCreateTrips } from './BulkCreateTrips';
import type { Trip, TripStatus, Route, RouteFare, Vehicle, Driver } from '../../../types/api';

interface CreateForm {
  route: string;
  vehicle: string;
  driver: string;
  departure_time: string;
  expected_capacity: number;
}

const emptyForm: CreateForm = {
  route: '', vehicle: '', driver: '', departure_time: '',
  expected_capacity: 14,
};

const Trips: React.FC = () => {
  const navigate = useNavigate();
  const { data: trips, loading, refetch } = usePaginatedResource<Trip>('/trips/');

  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState<Trip | null>(null);
  const [form, setForm] = useState<CreateForm>(emptyForm);
  const [cancelTarget, setCancelTarget] = useState<Trip | null>(null);
  const [previewTrip, setPreviewTrip] = useState<Trip | null>(null);
  const [previewRoute, setPreviewRoute] = useState<Route | null>(null);
  const [previewFares, setPreviewFares] = useState<RouteFare[]>([]);

  // Options for the searchable Route / Vehicle / Driver pickers.
  const [routes, setRoutes] = useState<Route[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    Promise.all([listRoutes(), listVehicles(), listDrivers()])
      .then(([r, v, d]) => { setRoutes(r); setVehicles(v); setDrivers(d); })
      .catch(() => { /* leave empty; fields just show no options */ })
      .finally(() => setOptionsLoading(false));
  }, []);

  const routeOptions = useMemo(
    () => routes
      .filter((r) => r.is_active)
      .map((r) => ({
        value: String(r.id),
        label: r.name || [r.origin_name, r.destination_name].filter(Boolean).join(' → ') || `Route #${r.id}`,
      })),
    [routes],
  );
  const vehicleOptions = useMemo(
    () => vehicles.map((v) => ({
      value: String(v.id),
      label: `${v.registration_number} — ${v.capacity} seats`,
    })),
    [vehicles],
  );
  const driverOptions = useMemo(
    () => drivers.map((d) => ({
      value: String(d.id),
      label: `${d.full_name?.trim() || d.phone || `Driver #${d.id}`} — ${d.license_number}`,
    })),
    [drivers],
  );

  const saveTrip = useResourceMutation(
    async (state: CreateForm) => {
      const payload = {
        route: parseInt(state.route, 10),
        vehicle: state.vehicle ? parseInt(state.vehicle, 10) : null,
        driver: state.driver ? parseInt(state.driver, 10) : null,
        departure_time: state.departure_time,
        expected_capacity: state.expected_capacity,
      };
      return editing ? updateTrip(editing.id, payload) : createTrip(payload);
    },
    { successMessage: 'Trip saved' }
  );

  const updateStatus = useResourceMutation(
    async ({ id, status }: { id: number; status: TripStatus }) => {
      await api.post(`/trips/${id}/update_status/`, { status });
    },
    { successMessage: 'Status updated' }
  );

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (t: Trip) => {
    setEditing(t);
    setForm({
      route: String(t.route),
      vehicle: t.vehicle ? String(t.vehicle) : '',
      driver: t.driver ? String(t.driver) : '',
      departure_time: t.departure_time.slice(0, 16),
      expected_capacity: t.expected_capacity ?? 14,
    });
    setShowForm(true);
  };

  const openFarePreview = async (trip: Trip) => {
    try {
      const [detail, route] = await Promise.all([
        getTripDetail(trip.id),
        getRoute(trip.route),
      ]);
      setPreviewTrip(detail);
      setPreviewRoute(route);
      setPreviewFares((detail.trip_fares ?? []).map(tf => ({
        id: tf.id,
        boarding_stop_id: tf.boarding_stop_id,
        alighting_stop_id: tf.alighting_stop_id,
        amount: tf.amount,
      })));
    } catch { /* silently ignore — preview is best-effort */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const saved = await saveTrip.run(form);
      setShowForm(false);
      refetch();
      if (saved) {
        openFarePreview(saved);
      }
    } catch { /* toast handled by hook */ }
  };

  const handleStatus = async (trip: Trip, status: TripStatus) => {
    try {
      await updateStatus.run({ id: trip.id, status });
      refetch();
    } catch { /* toast handled */ }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await updateStatus.run({ id: cancelTarget.id, status: 'cancelled' });
      setCancelTarget(null);
      refetch();
    } catch { /* toast handled */ }
  };

  const columns: Column<Trip>[] = [
    { key: 'route',        header: 'Route',     render: (t) => t.route_name ?? `#${t.route}` },
    { key: 'origin_name', header: 'From', render: (t) => t.origin_name ?? '—' },
    { key: 'destination_name', header: 'To', render: (t) => t.destination_name ?? '—' },
    { key: 'vehicle',      header: 'Vehicle',
      render: (t) => t.vehicle_reg ?? (t.vehicle ? `#${t.vehicle}` : '—') },
    { key: 'driver',       header: 'Driver',
      render: (t) => t.driver_name ?? (t.driver ? `#${t.driver}` : '—') },
    { key: 'departure_time', header: 'Departure', sortable: true,
      render: (t) => new Date(t.departure_time).toLocaleString() },
    { key: 'fare',         header: 'End-to-end fare', render: (t) => `KES ${t.fare}` },
    { key: 'available_seats', header: 'Seats', sortable: true },
    { key: 'status',       header: 'Status',    filterable: true, filterOptions: [
      { value: 'scheduled',  label: 'Scheduled' },
      { value: 'boarding',   label: 'Boarding' },
      { value: 'in_transit', label: 'In transit' },
      { value: 'completed',  label: 'Completed' },
      { value: 'cancelled',  label: 'Cancelled' },
    ], render: (t) => <StatusBadge variant="trip" status={t.status} /> },
  ];

  const rowActions = (trip: Trip) => (
    <div className="inline-flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={() => openEdit(trip)}>Edit</Button>
      <Button variant="ghost" size="sm" onClick={() => openFarePreview(trip)}>Fares</Button>
      <Button
        variant="ghost" size="sm"
        onClick={() => navigate(`/admin/operations/trips/${trip.id}/manifest`)}
      >Manifest</Button>
      {trip.status === 'scheduled' && (
        <Button variant="secondary" size="sm" onClick={() => handleStatus(trip, 'boarding')}>Board</Button>
      )}
      {trip.status === 'boarding' && (
        <Button variant="primary" size="sm" onClick={() => handleStatus(trip, 'in_transit')}>Depart</Button>
      )}
      {trip.status === 'in_transit' && (
        <Button variant="primary" size="sm" onClick={() => handleStatus(trip, 'completed')}>Complete</Button>
      )}
      {!['completed', 'cancelled'].includes(trip.status) && (
        <Button variant="danger" size="sm" onClick={() => setCancelTarget(trip)}>Cancel</Button>
      )}
    </div>
  );

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = trips.filter((t) => {
      const d = new Date(t.departure_time);
      return d >= today && d < new Date(today.getTime() + 86400000);
    }).length;
    const active = trips.filter((t) =>
      ['scheduled', 'boarding', 'in_transit'].includes(t.status)
    ).length;
    const completed = trips.filter((t) => t.status === 'completed').length;
    const cancelled = trips.filter((t) => t.status === 'cancelled').length;
    return { todayCount, active, completed, cancelled };
  }, [trips]);

  return (
    <>
      <PageHeader
        title="Trips"
        description="Scheduled and historical trips across all routes."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowBulk(true)} icon={<Plus className="h-4 w-4" />}>
              Bulk create
            </Button>
            <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
              New trip
            </Button>
          </div>
        }
      />

      <StatStrip
        className="mb-6"
        stats={[
          { label: 'Today', value: stats.todayCount, icon: <Bus className="h-4 w-4" /> },
          { label: 'Active', value: stats.active, icon: <Clock className="h-4 w-4" /> },
          { label: 'Completed', value: stats.completed, icon: <CheckCircle2 className="h-4 w-4" /> },
          { label: 'Cancelled', value: stats.cancelled, icon: <Ban className="h-4 w-4" /> },
        ]}
      />

      <DataTable<Trip>
        columns={columns}
        data={trips}
        rowKey="id"
        loading={loading}
        emptyTitle="No trips yet"
        emptyDescription="Schedule your first trip to get started."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New trip
          </Button>
        }
        actions={rowActions}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit trip' : 'New trip'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saveTrip.loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={saveTrip.loading}
              disabled={!form.route || !form.departure_time}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <form id="trip-create" onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Route" name="route" required error={saveTrip.fieldErrors.route}>
            <SearchableSelect
              id="route"
              options={routeOptions}
              value={form.route}
              onChange={(v) => setForm({ ...form, route: v })}
              loading={optionsLoading}
              placeholder="Select a route…"
              searchPlaceholder="Search routes…"
              aria-label="Route"
            />
          </FormField>
          <FormField label="Vehicle (optional — assign later)" name="vehicle" error={saveTrip.fieldErrors.vehicle}>
            <SearchableSelect
              id="vehicle"
              options={vehicleOptions}
              value={form.vehicle}
              onChange={(v) => setForm({ ...form, vehicle: v })}
              loading={optionsLoading}
              placeholder="Assign later…"
              searchPlaceholder="Search by registration…"
              aria-label="Vehicle"
            />
          </FormField>
          <FormField label="Driver (optional — assign later)" name="driver" error={saveTrip.fieldErrors.driver}>
            <SearchableSelect
              id="driver"
              options={driverOptions}
              value={form.driver}
              onChange={(v) => setForm({ ...form, driver: v })}
              loading={optionsLoading}
              placeholder="Select a driver…"
              searchPlaceholder="Search by name or licence…"
              aria-label="Driver"
            />
          </FormField>
          <FormField label="Departure" name="departure_time" required error={saveTrip.fieldErrors.departure_time}>
            <Input id="departure_time" type="datetime-local" required value={form.departure_time}
              onChange={(e) => setForm({ ...form, departure_time: e.target.value })}
            />
          </FormField>
          <FormField label="Seats" name="expected_capacity" error={saveTrip.fieldErrors.expected_capacity}>
            <select
              id="expected_capacity"
              aria-label="Seats"
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
              value={form.expected_capacity}
              onChange={(e) => setForm({ ...form, expected_capacity: parseInt(e.target.value, 10) })}
            >
              <option value={14}>14-seater matatu (default)</option>
              <option value={22}>22-seater</option>
              <option value={33}>33-seater</option>
              <option value={51}>51-seater coach</option>
              <option value={62}>62-seater coach</option>
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Used when no vehicle is picked. Once a bus is assigned, its real
              capacity takes over.
            </p>
          </FormField>
        </form>
      </Modal>

      <Modal
        isOpen={showBulk}
        onClose={() => setShowBulk(false)}
        title="Bulk create trips"
        size="lg"
      >
        <BulkCreateTrips
          routeOptions={routeOptions}
          vehicleOptions={vehicleOptions}
          driverOptions={driverOptions}
          optionsLoading={optionsLoading}
          onClose={() => setShowBulk(false)}
          onCreated={() => { setShowBulk(false); refetch(); }}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!cancelTarget}
        title="Cancel trip?"
        message={`Cancelling will notify booked passengers. This cannot be undone.`}
        confirmLabel="Cancel trip"
        variant="danger"
        loading={updateStatus.loading}
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />

      {previewTrip && (
        <Modal
          isOpen={!!previewTrip}
          onClose={() => { setPreviewTrip(null); setPreviewRoute(null); setPreviewFares([]); }}
          title={`Trip fares — ${previewTrip.route_name ?? `Trip ${previewTrip.id}`}`}
          size="lg"
        >
          <div>
            <p className="mb-3 text-sm text-slate-600">
              These fares were snapshotted from the route&apos;s fare matrix when the trip was created.
              {!previewTrip.is_fare_locked && ' Edit amounts below and save to override for this trip.'}
            </p>
            <RouteFareMatrix
              stops={previewRoute?.stops ?? []}
              fares={previewFares}
              onChange={setPreviewFares}
              disabled={previewTrip.is_fare_locked === true}
            />
            {previewTrip.is_fare_locked ? (
              <p className="mt-3 text-xs text-amber-700">
                Locked — trip has paid bookings or has already departed.
              </p>
            ) : (
              <Button
                className="mt-3"
                onClick={async () => {
                  await setTripFares(previewTrip.id, previewFares.map(f => ({
                    boarding_stop_id: f.boarding_stop_id,
                    alighting_stop_id: f.alighting_stop_id,
                    amount: f.amount,
                  })));
                  const refreshed = await getTripDetail(previewTrip.id);
                  setPreviewTrip(refreshed);
                }}
              >
                Save trip fares
              </Button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default Trips;
